/**
 * @file HostingPage.jsx
 * @description A fully responsive page for Marmasco Technologies,
 *              showcasing our reliable and affordable Shared Hosting solutions,
 *              ideal for personal websites, blogs, and small businesses.
 *              Features include cPanel, 1-Click Installs, Business Email, and SSD Storage.
 * @author
 *   Masimba Maregedze
 * @version 1.7.0
 */

import React, { useState, useEffect } from "react";
// Import Image from react-bootstrap
import { Container, Row, Col, Spinner, Image } from "react-bootstrap";
import { Helmet } from "react-helmet";
import {
  FaEnvelope,
  FaServer, // Changed icon
  FaCogs, // Changed icon
  FaRocket, // Changed icon
  FaDatabase, // Changed icon
  FaCheckCircle,
  FaLock, // Added icon
} from "react-icons/fa";
import featImg from "../assets/shared-hosting.png"; // Keep or update image as needed
import { runData } from "../functions/api/processor";
import HostingOrderForm from "../order/HostingOrder"; // Updated import name
import PageHeader from "../components/PageHeader";

/**
 * @function HostingPage
 * @description Renders a professional Shared Hosting page detailing features, benefits,
 *              and an integrated order form for quick service signup.
 * @returns {JSX.Element} A production-ready Shared Hosting page component.
 */
const HostingPage = () => {
  // Dynamic SEO state using API fallback
  const [seoData, setSeoData] = useState({
    title: "Loading...", // Will be updated by API
    meta_description: "", // Will be updated by API
    keywords: "", // Will be updated by API
    og_title: "", // Will be updated by API
    og_description: "", // Will be updated by API
    og_image: "", // Use relevant hosting image
    canonical_url: window.location.href,
  });
  // State to manage loading and error status during SEO data fetching.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Use runData with 'GET' method for SEO data
        const response = await runData(
          { page: "hosting" },
          "/web/pages/",
          "GET"
        );
        if (response.status === 200 && response.data.seo) {
          let seoResponse = response.data.seo;
          if (
            seoResponse.canonical_url &&
            seoResponse.canonical_url.startsWith("/")
          ) {
            let hostname = window.location.hostname;
            if (!hostname.startsWith("www.")) {
              hostname = "www." + hostname;
            }
            seoResponse.canonical_url =
              window.location.protocol +
              "//" +
              hostname +
              seoResponse.canonical_url;
          }
          setSeoData(seoResponse);
        }
      } catch (err) {
        console.error("Failed to load SEO data:", err);
        setError(err.message || "An error occurred while loading SEO data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Display an animated loading spinner while SEO data is being fetched.
  if (loading) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  // Display an error message if SEO data fails to load.
  if (error) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <h2>Error: {error}</h2>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{seoData.title} - Marmasco Technologies</title>
        <meta name="description" content={seoData.meta_description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta property="og:title" content={seoData.og_title} />
        <meta property="og:description" content={seoData.og_description} />
        <meta property="og:image" content={seoData.og_image} />
        <link rel="canonical" href={seoData.canonical_url} />
        <script type="application/ld+json">
          {`
            {
              "@context": "http://schema.org",
              "@type": "WebPage",
              "name": "${seoData.title} - Marmasco Technologies",
              "url": "${seoData.canonical_url}",
              "description": "${seoData.meta_description}"
            }
          `}
        </script>
      </Helmet>

      {/* Header Section */}
      <PageHeader seoData={seoData} />

      {/* Main Content Section */}
      {/* Apply consistent container styling */}
      <Container
        id="content-start"
        className="bg-light fade-in rounded-4 shadow-lg pt-5 py-5" // Updated classes and padding
        style={{
          marginTop: "-6rem",
          borderRadius: "1rem",
          position: "relative",
          zIndex: 1,
        }} // Updated styles
      >
        {/* Section Header with Title and Lead Text */}
        <Row className="mb-5">
          <Col className="text-center px-md-4 px-2">
            {" "}
            {/* Adjusted padding */}
            {/* Apply consistent heading and paragraph styling */}
            <h2 className="display-5 fw-bold mb-3 section-title">
              Get Online Quickly
            </h2>
            <p
              className="lead text-muted mx-auto"
              style={{ maxWidth: "800px" }}
            >
              Get your website online quickly and easily with our feature-rich
              shared hosting plans. Perfect for personal sites, blogs, and small
              businesses looking for performance and value.
            </p>
          </Col>
        </Row>

        {/* Apply consistent padding to content row */}
        <Row className="align-items-center px-md-4 px-2">
          {/* Textual Content Section - Updated for Shared Hosting */}
          <Col md={6} className="d-flex align-items-start">
            {" "}
            {/* Removed specific px */}
            <div>
              <p className="mb-3" style={{ textAlign: "justify" }}>
                Our Shared Hosting provides a powerful, cost-effective solution
                packed with essential tools. Manage your website effortlessly
                with the industry-leading cPanel control panel and install
                popular applications like WordPress in just one click.
              </p>
              {/* Updated Feature List for Shared Hosting */}
              <ul
                className="list-unstyled text-muted mt-4 mb-4"
                style={{ paddingLeft: "0px" }} // Removed excessive padding
              >
                <li className="d-flex align-items-center mb-3">
                  {" "}
                  {/* Increased margin-bottom */}
                  <FaEnvelope size={20} className="me-3 text-primary" />{" "}
                  {/* Added color */}
                  <span>Professional Business Email Hosting</span>
                  <FaCheckCircle
                    size={20}
                    className="ms-auto text-success"
                  />{" "}
                  {/* Changed color */}
                </li>
                <li className="d-flex align-items-center mb-3">
                  <FaServer size={20} className="me-3 text-primary" />
                  <span>Generous SSD Storage Space</span>
                  <FaCheckCircle size={20} className="ms-auto text-success" />
                </li>
                <li className="d-flex align-items-center mb-3">
                  <FaCogs size={20} className="me-3 text-primary" />
                  <span>Easy-to-Use cPanel Control Panel</span>
                  <FaCheckCircle size={20} className="ms-auto text-success" />
                </li>
                <li className="d-flex align-items-center mb-3">
                  <FaRocket size={20} className="me-3 text-primary" />
                  <span>1-Click Application Installer (Softaculous)</span>
                  <FaCheckCircle size={20} className="ms-auto text-success" />
                </li>
                <li className="d-flex align-items-center mb-3">
                  <FaDatabase size={20} className="me-3 text-primary" />
                  <span>MySQL Databases & PHP Support</span>
                  <FaCheckCircle size={20} className="ms-auto text-success" />
                </li>
                <li className="d-flex align-items-center mb-3">
                  <FaLock size={20} className="me-3 text-primary" />
                  <span>Free SSL Certificate Included</span>
                  <FaCheckCircle size={20} className="ms-auto text-success" />
                </li>
              </ul>
              <p className="mt-3" style={{ textAlign: "justify" }}>
                Benefit from reliable performance, 99.9% uptime guarantee, and
                our dedicated 24/7 customer support. Start building your online
                presence today!
              </p>
            </div>
          </Col>

          {/* Image Section */}
          <Col
            md={6}
            className="d-flex flex-column align-items-center justify-content-center mt-4 mt-md-0"
          >
            {/* Display the hosting feature image */}
            <Image
              src={featImg}
              alt="Shared Hosting Features"
              fluid // Makes the image responsive
              rounded // Adds rounded corners
            />
          </Col>
        </Row>
      </Container>
      <Container className="mt-5 px-4 py-4">
        <Row className="justify-content-center">
          {" "}
          {/* Center the column */}
          <div className="text-center mt-4">
            <h3 className="fw-bold section-title">Ready to Get Started?</h3>
            <p className="text-muted">
              Configure your plan and get started in minutes.
            </p>
          </div>
          <HostingOrderForm />
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default HostingPage;
